import type { ClientInvoicingInstructionsRequest } from '@contracts/client/ClientInvoicingInstructionsRequest';
import type { ClientListResponse } from '@contracts/client/ClientListResponse';
import type { ClientPaymentMethodRequest } from '@contracts/client/ClientPaymentMethodRequest';
import type { ClientRequest } from '@contracts/client/ClientRequest';
import type { ClientRequirePORequest } from '@contracts/client/ClientRequirePORequest';
import type {
  ClientResponse,
  DeliveryTypeOptionResponse,
} from '@contracts/client/ClientResponse';
import type { ClientSummaryListResponse } from '@contracts/client/ClientSummaryListResponse';
import type { QuickbooksMapping } from '@contracts/client/QuickbooksMapping';
import type { ClientDeliveryTypeOption } from '@src/clients/domain/Client';
import {
  Client,
  ClientList,
  ClientSummaryList,
  DeliveryTypeOption,
} from '@src/clients/domain/Client';
import { Agency } from '@src/clientUser/agency/Agency';
import { AuthenticatedClient } from '@src/common/services/AuthenticatedClient';

export const ClientsClient = {
  create: async (data: ClientRequest): Promise<Client> => {
    const { data: response } = await AuthenticatedClient.post<ClientResponse>(
      'clients',
      data,
    );

    return assembleClient(response);
  },
  update: async (clientId: string, data: ClientRequest): Promise<Client> => {
    const { data: response } = await AuthenticatedClient.put<ClientResponse>(
      `clients/${clientId}`,
      data,
    );

    return assembleClient(response);
  },
  archive: async (clientId: string): Promise<void> => {
    await AuthenticatedClient.put(`clients/${clientId}/archive`);
  },
  unarchive: async (clientId: string): Promise<void> => {
    await AuthenticatedClient.put(`clients/${clientId}/unarchive`);
  },
  updatePaymentMethod: async (
    clientId: string,
    data: ClientPaymentMethodRequest,
  ): Promise<Client> => {
    const { data: response } = await AuthenticatedClient.post<ClientResponse>(
      `clients/${clientId}:setPaymentMethod`,
      data,
    );

    return assembleClient(response);
  },
  updateRequirePO: async (
    clientId: string,
    data: ClientRequirePORequest,
  ): Promise<Client> => {
    const { data: response } = await AuthenticatedClient.post<ClientResponse>(
      `clients/${clientId}:setRequirePO`,
      data,
    );

    return assembleClient(response);
  },
  updateInvoicingInstructions: async (
    clientId: string,
    data: ClientInvoicingInstructionsRequest,
  ): Promise<Client> => {
    const { data: response } = await AuthenticatedClient.post<ClientResponse>(
      `clients/${clientId}:setInvoicingInstructions`,
      data,
    );

    return assembleClient(response);
  },
  updateDefaultBillTo: async ({
    clientId,
    defaultBillToClientId,
  }: {
    clientId: string;
    defaultBillToClientId?: string;
  }): Promise<void> => {
    await AuthenticatedClient.post<ClientResponse>(
      `clients/${clientId}:setDefaultBillTo`,
      { defaultBillToClientId },
    );
  },
  updateQuickbooksMapping: async ({
    clientId,
    mapping,
  }: {
    clientId: string;
    mapping: QuickbooksMapping;
  }): Promise<Client> => {
    const { data: response } = await AuthenticatedClient.post<ClientResponse>(
      `clients/${clientId}:setQuickbooksMapping`,
      mapping,
    );
    return assembleClient(response);
  },
  listSummary: async (): Promise<ClientSummaryList> => {
    const { data: response } =
      await AuthenticatedClient.get<ClientSummaryListResponse>(
        'clients/summary',
      );

    return new ClientSummaryList(response.clients);
  },
  list: async (): Promise<ClientList> => {
    const { data: response } =
      await AuthenticatedClient.get<ClientListResponse>('clients');

    return new ClientList(
      response.clients.map((rawClient) => assembleClient(rawClient)),
    );
  },
  get: async (clientId: string): Promise<Client> => {
    const { data: response } = await AuthenticatedClient.get<ClientResponse>(
      `clients/${clientId}`,
    );

    return assembleClient(response);
  },
  listAllWithClientAsBillTo: async (clientId: string): Promise<ClientList> => {
    const { data: response } =
      await AuthenticatedClient.get<ClientListResponse>('clients/asBillTo', {
        params: {
          clientId,
        },
      });

    return new ClientList(
      response.clients.map((rawClient) => assembleClient(rawClient)),
    );
  },
};

const assembleClientAttributes = (response: ClientResponse) => ({
  ...response,
  primaryDeliveryType: {
    deliveryType: assembleDeliveryType(
      response.primaryDeliveryType.deliveryType,
    ),
    value: response.primaryDeliveryType.value,
  },
  secondaryDeliveryType: response.secondaryDeliveryType
    ? {
        deliveryType: assembleDeliveryType(
          response.secondaryDeliveryType.deliveryType,
        ),
        value: response.secondaryDeliveryType.value,
      }
    : undefined,
});

const assembleClient = (response: ClientResponse) => {
  const attributes = assembleClientAttributes(response);

  if (attributes.isAgency) {
    return new Agency({
      ...attributes,
      isAgency: true,
      clients:
        response.clients?.map(
          (client) => new Client(assembleClientAttributes(client)),
        ) ?? [],
    });
  }

  return new Client(attributes);
};

const assembleDeliveryType = (
  option: DeliveryTypeOptionResponse,
): ClientDeliveryTypeOption => {
  switch (option) {
    case 'email':
      return DeliveryTypeOption.EMAIL;
    case 'fax':
      return DeliveryTypeOption.FAX;
    case 'mail':
      return DeliveryTypeOption.MAIL;
    case 'webDownload':
      return DeliveryTypeOption.WEB_DOWNLOAD;
    case 'sms':
      throw new Error('SMS is not a valid client delivery type');
  }
};
